import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import {
  CardapioMinishopModoPreco,
  CardapioMinishopProdutoDTO,
  CardapioMinishopProdutoFormDTO,
  VarianteProdutoCardapioDTO,
  VarianteProdutoCardapioConsultaDTO,
  ProdutoGrupoModificadorConsultaDTO,
} from '../../../../../models/cardapio-minishop/cardapio-minishop.model';
import { CardapioMinishopVarianteService } from '../../../../../services/cardapio-minishop/cardapio-minishop-variante.service';
import { ProdutoGrupoModificadorService } from '../../../../../services/cardapio-minishop/produto-grupo-modificador.service';
import { ImagemUtils } from '../../../../../shared/utils/imagem-utils';

@Component({
  selector: 'app-cardapio-minishop-produto-edit',
  templateUrl: './cardapio-minishop-produto-edit.component.html',
  styleUrls: ['./cardapio-minishop-produto-edit.component.scss'],
  providers: [MessageService],
})
export class CardapioMinishopProdutoEditComponent implements OnInit, OnDestroy, OnChanges {
  @Input() produto: CardapioMinishopProdutoDTO | null = null;
  @Input() categoriaId = 0;
  @Input() visivel = false;
  @Input() moedaSimbolo = 'BRL';
  @Input() moedaLocal = 'pt-BR';
  @Input() salvando = false;

  @Output() visivelChange = new EventEmitter<boolean>();
  @Output() salvar = new EventEmitter<CardapioMinishopProdutoFormDTO>();
  @Output() cancelar = new EventEmitter<void>();

  // Formulario
  form: CardapioMinishopProdutoFormDTO = this.createEmptyForm();

  // Opcoes do toggle de preco
  modoPrecoOptions: { label: string; value: CardapioMinishopModoPreco }[] = [];

  // Estado da imagem
  imagePreviewUrl: string | null = null;
  fileName: string | null = null;

  // Variantes e Modificadores (novos endpoints)
  variantes: VarianteProdutoCardapioConsultaDTO[] = [];
  gruposAssociados: ProdutoGrupoModificadorConsultaDTO[] = [];
  loadingVariantes = false;
  loadingModificadores = false;
  modalModificadorVisivel = false;

  texts = {
    editar_produto: '',
    novo_produto: '',
    desc_editar: '',
    desc_novo: '',
    nome: '',
    descricao: '',
    preco: '',
    preco_simples: '',
    preco_variantes: '',
    preco_original: '',
    preco_promocional: '',
    adicionar_variante: '',
    nome_variante: '',
    controle_estoque: '',
    info_estoque: '',
    cozinha: '',
    selecione_area: '',
    modificador_titulo: '',
    modificador_descricao: '',
    adicionar_modificador: '',
    salvar: '',
    cancelar: '',
    visivel_cardapio: '',
    erro: '',
    erro_generico: '',
    sucesso_criar_variante: '',
    sucesso_atualizar_variante: '',
    sucesso_excluir_variante: '',
    sucesso_reordenar_variantes: '',
    confirmar_excluir_variante: '',
    sucesso_desassociar: '',
    sucesso_reordenar_modificadores: '',
  };

  private langChangeSub!: Subscription;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private varianteService: CardapioMinishopVarianteService,
    private produtoGrupoService: ProdutoGrupoModificadorService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.buildModoPrecoOptions();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.buildModoPrecoOptions();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) this.langChangeSub.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['produto'] || changes['visivel']) {
      if (this.visivel) {
        this.initForm();
        if (this.produto?.id) {
          this.carregarVariantes(this.produto.id);
          this.carregarModificadores(this.produto.id);
        } else {
          this.variantes = [];
          this.gruposAssociados = [];
        }
      }
    }
  }

  get tituloSidebar(): string {
    return this.produto ? this.texts.editar_produto : this.texts.novo_produto;
  }

  get descricaoSidebar(): string {
    return this.produto ? this.texts.desc_editar : this.texts.desc_novo;
  }

  // --- Inicializacao do formulario ---

  private initForm(): void {
    if (this.produto) {
      this.form = {
        nome: this.produto.nome,
        descricao: this.produto.descricao || '',
        tipoPreco: this.produto.tipoPreco || 'SIMPLES',
        preco: this.produto.preco,
        precoPromocional: this.produto.precoPromocional || null,
        variantes: [],
        modificadores: [],
        controleEstoque: this.produto.controleEstoque,
        cozinha: this.produto.impressoraPadraoId
          ? { label: `#${this.produto.impressoraPadraoId}`, value: this.produto.impressoraPadraoId }
          : undefined,
        foto: this.produto.foto || null,
        fotoBase64: null,
        visivel: this.produto.visivel,
      };
      this.imagePreviewUrl = this.produto.fotoUrl || this.produto.foto || null;
    } else {
      this.form = this.createEmptyForm();
      this.imagePreviewUrl = null;
    }
    this.fileName = null;
  }

  private createEmptyForm(): CardapioMinishopProdutoFormDTO {
    return {
      nome: '',
      descricao: '',
      tipoPreco: 'SIMPLES',
      preco: null,
      precoPromocional: null,
      variantes: [],
      modificadores: [],
      controleEstoque: false,
      cozinha: undefined,
      foto: null,
      fotoBase64: null,
      visivel: true,
    };
  }

  // --- Upload de imagem ---

  async onSelectImage(event: any): Promise<void> {
    const file: File | undefined = event.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    this.fileName = file.name;
    const imagem = await ImagemUtils.comprimirParaBase64(file);
    this.imagePreviewUrl = imagem.dataUrl;
    this.form.fotoBase64 = imagem.base64;
  }

  onClearImage(): void {
    this.fileName = null;
    this.imagePreviewUrl = null;
    this.form.foto = null;
    this.form.fotoBase64 = null;
  }

  // --- Carregar dados via API ---

  carregarVariantes(produtoId: number): void {
    this.loadingVariantes = true;
    this.varianteService.listar(produtoId).subscribe({
      next: (data) => {
        this.variantes = data;
        this.loadingVariantes = false;
      },
      error: (err) => {
        this.handleError(err);
        this.loadingVariantes = false;
      },
    });
  }

  carregarModificadores(produtoId: number): void {
    this.loadingModificadores = true;
    this.produtoGrupoService.listar(produtoId).subscribe({
      next: (data) => {
        this.gruposAssociados = data;
        this.loadingModificadores = false;
      },
      error: (err) => {
        this.handleError(err);
        this.loadingModificadores = false;
      },
    });
  }

  // --- Handlers de Variantes ---

  onCriarVariante(dto: VarianteProdutoCardapioDTO): void {
    this.varianteService.criar(dto).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: this.texts.sucesso_criar_variante, life: 3000 });
        if (this.produto?.id) this.carregarVariantes(this.produto.id);
      },
      error: (err) => this.handleError(err),
    });
  }

  onAtualizarVariante(event: { id: number; dto: VarianteProdutoCardapioDTO }): void {
    this.varianteService.atualizar(event.id, event.dto).subscribe({
      next: (varianteAtualizada) => {
        // Atualizar na lista local
        const idx = this.variantes.findIndex(v => v.id === event.id);
        if (idx >= 0) this.variantes[idx] = varianteAtualizada;
        this.messageService.add({ severity: 'success', summary: this.texts.sucesso_atualizar_variante, life: 3000 });
      },
      error: (err) => this.handleError(err),
    });
  }

  onRemoverVariante(id: number): void {
    this.varianteService.remover(id).subscribe({
      next: () => {
        this.variantes = this.variantes.filter(v => v.id !== id);
        this.messageService.add({ severity: 'success', summary: this.texts.sucesso_excluir_variante, life: 3000 });
      },
      error: (err) => this.handleError(err),
    });
  }

  onReordenarVariantes(ids: number[]): void {
    this.varianteService.reordenar({ ids }).subscribe({
      error: (err) => {
        this.handleError(err);
        // Recarregar para reverter
        if (this.produto?.id) this.carregarVariantes(this.produto.id);
      },
    });
  }

  onToggleAtivoVariante(event: { id: number; ativo: boolean }): void {
    const variante = this.variantes.find(v => v.id === event.id);
    if (!variante) return;

    const dto: VarianteProdutoCardapioDTO = {
      nome: variante.nome,
      preco: variante.preco,
      precoPromocional: variante.precoPromocional || null,
      ordem: variante.ordem,
      produtoId: variante.produtoId,
      ativo: event.ativo,
      custo: variante.custo || null,
      desconto: variante.desconto || null,
      sku: variante.sku || null,
    };

    this.varianteService.atualizar(event.id, dto).subscribe({
      next: (atualizada) => {
        const idx = this.variantes.findIndex(v => v.id === event.id);
        if (idx >= 0) this.variantes[idx] = atualizada;
      },
      error: (err) => this.handleError(err),
    });
  }

  // --- Handlers de Modificadores ---

  onDesassociarGrupo(associacaoId: number): void {
    this.produtoGrupoService.desassociar(associacaoId).subscribe({
      next: () => {
        this.gruposAssociados = this.gruposAssociados.filter(g => g.id !== associacaoId);
        this.messageService.add({ severity: 'success', summary: this.texts.sucesso_desassociar, life: 3000 });
      },
      error: (err) => this.handleError(err),
    });
  }

  onReordenarGrupos(ids: number[]): void {
    this.produtoGrupoService.reordenar({ ids }).subscribe({
      error: (err) => {
        this.handleError(err);
        if (this.produto?.id) this.carregarModificadores(this.produto.id);
      },
    });
  }

  onGrupoAssociado(assoc: ProdutoGrupoModificadorConsultaDTO): void {
    this.gruposAssociados = [...this.gruposAssociados, assoc];
  }

  onGrupoDesassociadoFromModal(associacaoId: number): void {
    this.gruposAssociados = this.gruposAssociados.filter(g => g.id !== associacaoId);
  }

  onGrupoAtualizado(): void {
    // Recarregar para refletir mudancas nos grupos/opcoes
    if (this.produto?.id) this.carregarModificadores(this.produto.id);
  }

  // --- Salvar ---

  onSubmit(): void {
    if (!this.form.nome.trim()) return;
    this.salvar.emit(this.form);
  }

  onCancelar(): void {
    this.visivelChange.emit(false);
    this.cancelar.emit();
  }

  onSidebarHide(): void {
    this.visivelChange.emit(false);
  }

  // --- Helpers ---

  private buildModoPrecoOptions(): void {
    this.modoPrecoOptions = [
      { label: this.texts.preco_simples || 'Simples', value: 'SIMPLES' },
      { label: `${this.texts.preco_variantes || 'Variantes'} ${this.variantes.length}`, value: 'VARIANTES' },
    ];
  }

  trackByVarianteId(index: number, item: VarianteProdutoCardapioConsultaDTO): number {
    return item.id;
  }

  private handleError(error: any): void {
    const mensagem = error?.error?.message || this.texts.erro_generico;
    this.messageService.add({
      severity: 'error',
      summary: this.texts.erro,
      detail: mensagem,
      life: 5000,
    });
  }

  private loadTranslations(): void {
    this.texts = {
      editar_produto: this.translate.instant('cardapio_minishop.produto.editar_produto'),
      novo_produto: this.translate.instant('cardapio_minishop.produto.novo_produto'),
      desc_editar: this.translate.instant('cardapio_minishop.produto.desc_editar'),
      desc_novo: this.translate.instant('cardapio_minishop.produto.desc_novo'),
      nome: this.translate.instant('cardapio_minishop.produto.nome'),
      descricao: this.translate.instant('cardapio_minishop.produto.descricao'),
      preco: this.translate.instant('cardapio_minishop.produto.preco'),
      preco_simples: this.translate.instant('cardapio_minishop.produto.preco_simples'),
      preco_variantes: this.translate.instant('cardapio_minishop.produto.preco_variantes'),
      preco_original: this.translate.instant('cardapio_minishop.produto.preco_original'),
      preco_promocional: this.translate.instant('cardapio_minishop.produto.preco_promocional'),
      adicionar_variante: this.translate.instant('cardapio_minishop.produto.adicionar_variante'),
      nome_variante: this.translate.instant('cardapio_minishop.produto.nome_variante'),
      controle_estoque: this.translate.instant('cardapio_minishop.produto.controle_estoque'),
      info_estoque: this.translate.instant('cardapio_minishop.produto.info_estoque'),
      cozinha: this.translate.instant('cardapio_minishop.produto.cozinha'),
      selecione_area: this.translate.instant('cardapio_minishop.produto.selecione_area'),
      modificador_titulo: this.translate.instant('cardapio_minishop.modificador.titulo'),
      modificador_descricao: this.translate.instant('cardapio_minishop.modificador.descricao'),
      adicionar_modificador: this.translate.instant('cardapio_minishop.modificador.adicionar_modificador'),
      salvar: this.translate.instant('cardapio_minishop.comum.salvar'),
      cancelar: this.translate.instant('cardapio_minishop.comum.cancelar'),
      visivel_cardapio: this.translate.instant('cardapio_minishop.produto.visivel'),
      erro: this.translate.instant('cardapio_minishop.comum.erro'),
      erro_generico: this.translate.instant('cardapio_minishop.comum.erro_generico'),
      sucesso_criar_variante: this.translate.instant('cardapio_minishop.variante.sucesso_criar'),
      sucesso_atualizar_variante: this.translate.instant('cardapio_minishop.variante.sucesso_atualizar'),
      sucesso_excluir_variante: this.translate.instant('cardapio_minishop.variante.sucesso_excluir'),
      sucesso_reordenar_variantes: this.translate.instant('cardapio_minishop.variante.sucesso_reordenar'),
      confirmar_excluir_variante: this.translate.instant('cardapio_minishop.variante.confirmar_excluir'),
      sucesso_desassociar: this.translate.instant('cardapio_minishop.modificador.sucesso_desassociar'),
      sucesso_reordenar_modificadores: this.translate.instant('cardapio_minishop.modificador.sucesso_reordenar'),
    };
  }
}
